import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Scissors, Users, Calendar as CalendarIcon,
  Gift, User, LogOut, Plus, Edit2, Trash2, X, Download, Search, Mail, AlertTriangle, Reply,
  Menu, CheckCircle, Upload, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { translations } from '../context/LanguageContext';
import api, { formatApiErrorDetail } from '../utils/api';
import { toast } from 'sonner';
import { CustomerProfileDialog } from '../components/CustomerProfileDialog';

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'services', label: 'Services', icon: Scissors },
  { key: 'categories', label: 'Categories', icon: LayoutDashboard },
  { key: 'staff', label: 'Staff', icon: Users },
  { key: 'calendar', label: 'Calendar', icon: CalendarIcon },
  { key: 'promotions', label: 'Promotions', icon: Gift },
  { key: 'customers', label: 'Customers', icon: User },
  { key: 'availability', label: 'Availability', icon: CalendarIcon },
  { key: 'inbox', label: 'Inbox', icon: Mail },
  { key: 'gallery', label: 'Gallery', icon: Gift },
  { key: 'translations', label: 'Translations', icon: LayoutDashboard },
  { key: 'settings', label: 'Settings', icon: LayoutDashboard },
];

// Read an image File, resize to max 1000px, return a compressed JPEG data URL
const fileToResizedDataUrl = (file, maxDim = 1000, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round(height * (maxDim / width)); width = maxDim; }
        else { width = Math.round(width * (maxDim / height)); height = maxDim; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [data, setData] = useState({ services: [], staff: [], bookings: [], promotions: [], customers: [] });
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [s, st, b, p, c] = await Promise.all([
        api.get('/admin/services'),
        api.get('/admin/staff'),
        api.get('/admin/bookings'),
        api.get('/admin/promotions'),
        api.get('/admin/customers'),
      ]);
      setData({ services: s.data, staff: st.data, bookings: b.data, promotions: p.data, customers: c.data });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#faf6ec]" data-testid="admin-dashboard">
      {/* Mobile top bar with hamburger */}
      <div className="lg:hidden sticky top-0 z-50 flex items-center justify-between bg-[#0d3b25] text-[#c5a059] px-4 py-3 shadow-md">
        <button onClick={() => setSidebarOpen(true)} data-testid="admin-sidebar-open" className="p-1"><Menu className="w-6 h-6" /></button>
        <h1 className="text-lg font-serif font-bold">4AN Admin</h1>
        <span className="w-6" />
      </div>

      {/* Backdrop for mobile drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="admin-sidebar-backdrop" />
      )}

      <div className="flex min-h-screen">
        {/* Dark Sidebar (drawer on mobile, fixed on desktop) */}
        <aside className={`w-64 bg-[#0d3b25] text-white flex flex-col fixed h-screen top-0 left-0 z-50 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
          <div className="p-6 border-b border-[#c5a059]/20 flex items-center justify-between">
            <h1 className="text-2xl font-serif font-bold text-[#c5a059]">4AN Admin</h1>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white/70 hover:text-[#c5a059]" data-testid="admin-sidebar-close"><X className="w-5 h-5" /></button>
          </div>
          <nav className="flex-1 py-6 overflow-y-auto">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                onClick={() => { setActive(item.key); setSidebarOpen(false); }}
                data-testid={`admin-nav-${item.key}`}
                className={`w-full flex items-center space-x-3 px-6 py-3 text-sm transition-colors ${
                  active === item.key
                    ? 'bg-[#c5a059]/10 text-[#c5a059] border-r-2 border-[#c5a059]'
                    : 'text-white/70 hover:text-[#c5a059] hover:bg-white/5'
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
          <div className="p-6 border-t border-[#c5a059]/20 space-y-2">
            <Link to="/" className="block text-white/70 hover:text-[#c5a059] text-sm px-2 py-1">← Back to Site</Link>
            <button onClick={handleLogout} className="flex items-center space-x-2 text-white/70 hover:text-[#c5a059] text-sm px-2 py-1">
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 p-4 md:p-8">
          {loading ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c5a059]"></div>
            </div>
          ) : (
            <>
              {active === 'dashboard' && <DashboardTab data={data} />}
              {active === 'services' && <ServicesTab data={data} refresh={fetchAll} />}
              {active === 'categories' && <CategoriesTab />}
              {active === 'staff' && <StaffTab data={data} refresh={fetchAll} />}
              {active === 'calendar' && <CalendarTab data={data} refresh={fetchAll} />}
              {active === 'promotions' && <PromotionsTab data={data} refresh={fetchAll} />}
              {active === 'customers' && <CustomersTab data={data} refresh={fetchAll} />}
              {active === 'availability' && <AvailabilityTab data={data} />}
              {active === 'inbox' && <InboxTab />}
              {active === 'gallery' && <GalleryTab />}
              {active === 'translations' && <TranslationsTab />}
              {active === 'settings' && <SettingsTab />}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// ============ DASHBOARD TAB ============
const DashboardTab = ({ data }) => {
  const [dateFilter, setDateFilter] = useState('today');

  const now = new Date();
  now.setHours(0,0,0,0);
  
  const getFilteredBookings = () => {
    return data.bookings.filter((b) => {
      const bDate = new Date(b.date);
      bDate.setHours(0,0,0,0);
      
      if (dateFilter === 'today') {
        return bDate.getTime() === now.getTime();
      } else if (dateFilter === 'tomorrow') {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return bDate.getTime() === tomorrow.getTime();
      } else if (dateFilter === 'week') {
        const weekEnd = new Date(now);
        weekEnd.setDate(weekEnd.getDate() + 7);
        return bDate >= now && bDate <= weekEnd;
      }
      return true; // fallback
    });
  };

  const filteredBookings = getFilteredBookings();
  const revenue = filteredBookings.reduce((s, b) => s + (b.total_price || 0), 0);
  const noShows = filteredBookings.filter((b) => b.status === 'no-show').length;

  const upcoming = data.bookings
    .filter((b) => new Date(b.date) >= new Date() && b.status !== 'cancelled')
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5);

  const stats = [
    { label: 'Number of Appointments', value: filteredBookings.length },
    { label: 'No show', value: noShows },
    { label: 'Revenue', value: `€${revenue.toFixed(0)}` }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h2 className="text-3xl font-serif font-bold text-[#0d3b25]">Dashboard</h2>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-full border border-[#c5a059]/40 bg-white overflow-hidden" data-testid="dashboard-appointment-tabs">
            {[{ key: 'today', label: "Today's Appointments" }, { key: 'tomorrow', label: 'Tomorrow' }, { key: 'week', label: 'This Week' }].map((tab) => <button type="button" key={tab.key} onClick={() => setDateFilter(tab.key)} data-testid={`dashboard-appointment-tab-${tab.key}`} className={`px-3 sm:px-4 py-2 text-[10px] sm:text-xs font-semibold transition-colors ${dateFilter === tab.key ? 'bg-[#0d3b25] text-[#f5e9ce]' : 'text-[#0d3b25] hover:bg-[#c5a059]/10'}`}>{tab.label}</button>)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {stats.map((s, i) => (
          <div key={i} className="bg-white border border-[#c5a059]/20 rounded-sm p-6 shadow-sm">
            <p className="text-3xl font-bold text-[#0d3b25] mb-1">{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6">
        <h3 className="text-xl font-serif font-semibold text-[#0d3b25] mb-6">Upcoming Appointments</h3>
        {upcoming.length === 0 ? (
          <p className="text-muted-foreground text-sm">No upcoming appointments.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-[#c5a059]/20">
                <th className="pb-3">Date</th>
                <th className="pb-3">Time</th>
                <th className="pb-3">Client</th>
                <th className="pb-3">Service</th>
                <th className="pb-3">Staff</th>
                <th className="pb-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#c5a059]/10">
              {upcoming.map((b) => {
                const staff = b.staff || data.staff.find((s) => s.id === b.staff_id);
                const svcNames = (b.services || []).map((s) => s.name).join(', ');
                const promoTitle = b.promo?.title;
                return (
                  <tr key={b.id}>
                    <td className="py-4 text-sm">{b.date}</td>
                    <td className="py-4 text-sm">{b.time}</td>
                    <td className="py-4 text-sm font-medium">{b.client_name === '[deleted]' ? <span className="italic text-muted-foreground">Deleted client</span> : b.client_name}</td>
                    <td className="py-4 text-sm">
                      {promoTitle && (
                        <span className="inline-block bg-[#c5a059]/20 text-[#0d3b25] text-[10px] px-2 py-0.5 rounded-full mr-1 font-semibold">
                          BINGO: {promoTitle}
                        </span>
                      )}
                      <span className="text-xs">{svcNames || `${b.service_ids?.length || 0} service(s)`}</span>
                    </td>
                    <td className="py-4 text-sm">{staff?.name || '—'}</td>
                    <td className="py-4"><StatusBadge status={b.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const colors = {
    confirmed: 'bg-green-100 text-green-800',
    pending: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-200 text-gray-600',
    completed: 'bg-blue-100 text-blue-800',
    'no-show': 'bg-orange-100 text-orange-800',
  };
  return (
    <span className={`px-3 py-1 text-xs rounded-full font-medium ${colors[status] || 'bg-gray-100'}`}>
      {status || 'confirmed'}
    </span>
  );
};

// ============ SERVICES TAB ============
const ServicesTab = ({ data, refresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const filteredServices = data.services.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.name_en || '').toLowerCase().includes(q) ||
      (s.name_fr || '').toLowerCase().includes(q) ||
      (s.category || '').toLowerCase().includes(q)
    );
  }).sort((a, b) => {
    const catCmp = (a.category || '').localeCompare(b.category || '');
    if (catCmp !== 0) return catCmp;
    const soCmp = (a.sort_order || 100) - (b.sort_order || 100);
    if (soCmp !== 0) return soCmp;
    return (a.name || '').localeCompare(b.name || '');
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await api.delete(`/admin/services/${id}`);
      toast.success('Service deleted');
      refresh();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="text-3xl font-serif font-bold text-[#0d3b25]">Services</h2>
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search service..."
            className="w-64"
            data-testid="admin-service-search"
          />
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="admin-create-service-btn"
            className="bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e] border border-[#c5a059] rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" /> New Service
          </Button>
        </div>
      </div>
      <div className="bg-white border border-[#c5a059]/20 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0d3b25] text-[#c5a059]">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="px-6 py-4">Category</th>
              <th className="px-6 py-4">Sort</th>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Duration</th>
              <th className="px-6 py-4">Price</th>
              <th className="px-6 py-4">Tags</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#c5a059]/10">
            {filteredServices.map((s) => (
              <tr key={s.id} className="hover:bg-[#faf6ec]">
                <td className="px-6 py-4 text-sm font-semibold text-[#0d3b25]">{s.category}</td>
                <td className="px-6 py-4 text-sm text-muted-foreground">{s.sort_order || '—'}</td>
                <td className="px-6 py-4 text-sm font-medium">{s.name}</td>
                <td className="px-6 py-4 text-sm">{s.duration} min</td>
                <td className="px-6 py-4 text-sm font-semibold text-[#c5a059]">€{s.price.toFixed(2)}</td>
                <td className="px-6 py-4">
                  <div className="flex gap-1">
                    {s.featured && <span className="bg-[#c5a059] text-[#0d3b25] text-[10px] px-2 py-0.5 rounded-full font-semibold">⭐</span>}
                    {s.is_new && <span className="bg-green-100 text-green-800 text-[10px] px-2 py-0.5 rounded-full font-semibold">NEW</span>}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-1 rounded ${s.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                    {s.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 space-x-2">
                  <button
                    onClick={() => { setEditing(s); setDialogOpen(true); }}
                    className="bg-yellow-400 text-[#0d3b25] px-3 py-1 text-xs font-medium rounded hover:bg-yellow-500 inline-flex items-center"
                  >
                    <Edit2 className="w-3 h-3 mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="bg-red-500 text-white px-3 py-1 text-xs font-medium rounded hover:bg-red-600 inline-flex items-center"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ServiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editing={editing}
        refresh={refresh}
      />
    </div>
  );
};

const ServiceDialog = ({ open, onClose, editing, refresh }) => {
  const [form, setForm] = useState({
    name: '', name_en: '', name_fr: '',
    description: '', description_en: '', description_fr: '',
    category: 'Dameskapsels', category_key: 'womens_hair',
    price: '', duration: '', active: true,
    sort_order: 100, featured: false, is_new: false,
    image_url: '',
    staff_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [staffList, setStaffList] = useState([]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setUploading(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      setForm((f) => ({ ...f, image_url: dataUrl }));
      toast.success('Image ready — remember to save');
    } catch {
      toast.error('Could not process image');
    } finally { setUploading(false); e.target.value = ''; }
  };

  useEffect(() => {
    if (open) {
      api.get('/admin/staff').then((r) => setStaffList(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || '', name_en: editing.name_en || '', name_fr: editing.name_fr || '',
        description: editing.description || '', description_en: editing.description_en || '', description_fr: editing.description_fr || '',
        category: editing.category || 'Dameskapsels',
        category_key: editing.category_key || 'womens_hair',
        price: editing.price || '', duration: editing.duration || '',
        active: editing.active !== false,
        sort_order: editing.sort_order || 100,
        featured: !!editing.featured, is_new: !!editing.is_new,
        image_url: editing.image_url || '',
        staff_ids: editing.staff_ids || [],
      });
    } else {
      setForm({
        name: '', name_en: '', name_fr: '',
        description: '', description_en: '', description_fr: '',
        category: 'Dameskapsels', category_key: 'womens_hair',
        price: '', duration: '', active: true,
        sort_order: 100, featured: false, is_new: false,
        image_url: '',
        staff_ids: [],
      });
    }
  }, [editing, open]);

  const toggleStaff = (sid) => {
    setForm((f) => ({
      ...f,
      staff_ids: f.staff_ids.includes(sid) ? f.staff_ids.filter((x) => x !== sid) : [...f.staff_ids, sid]
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price), duration: parseInt(form.duration) };
    try {
      if (editing) {
        await api.put(`/admin/services/${editing.id}`, payload);
        toast.success('Service updated');
      } else {
        await api.post('/admin/services', payload);
        toast.success('Service created');
      }
      onClose();
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Service' : 'New Service'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-2">Name (Dutch)</label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-2">Name (English)</label>
              <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Name (French)</label>
              <Input value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Description (Dutch)</label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-2">Description (English)</label>
              <Textarea value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} rows={2} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Description (French)</label>
              <Textarea value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} rows={2} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Category</label>
            <select
              value={form.category_key}
              onChange={(e) => {
                const key = e.target.value;
                const catMap = {
                  womens_hair: 'Dameskapsels', mens_hair: 'Barbier / Herenkapsel',
                  hair_coloring: 'Haarkleuring & Behandeling', nail_care: 'Nagelverzorging',
                  waxing: 'Ontharing / Harsen / Threading', facial: 'Gezichtsbehandelingen'
                };
                setForm({ ...form, category_key: key, category: catMap[key] });
              }}
              className="w-full p-2 border rounded-sm"
            >
              <option value="womens_hair">Women's Hair</option>
              <option value="mens_hair">Barber / Men's Hair</option>
              <option value="hair_coloring">Hair Coloring & Treatments</option>
              <option value="nail_care">Nail Care</option>
              <option value="waxing">Hair Removal / Waxing / Threading</option>
              <option value="facial">Facial Services</option>
            </select>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium block mb-2">Price (€)</label>
              <Input type="number" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Duration (min)</label>
              <Input type="number" required value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Sort Order</label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 100 })} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Service Image</label>
            <div className="flex items-start gap-4">
              <div className="w-28 h-28 rounded-sm border border-[#c5a059]/30 bg-[#faf6ec] overflow-hidden flex items-center justify-center flex-shrink-0">
                {form.image_url ? (
                  <img src={form.image_url} alt="preview" className="w-full h-full object-cover" data-testid="service-image-preview" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-[#c5a059]/40" />
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer inline-flex items-center gap-2 bg-[#0d3b25] text-[#c5a059] border border-[#c5a059] rounded-full px-4 py-2 text-sm font-medium hover:bg-[#0a2e1e]" data-testid="service-image-upload-btn">
                  <Upload className="w-4 h-4" /> {uploading ? 'Processing…' : 'Upload Image'}
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                </label>
                {form.image_url && (
                  <button type="button" onClick={() => setForm({ ...form, image_url: '' })} className="ml-2 text-xs text-red-500 hover:underline" data-testid="service-image-remove-btn">Remove</button>
                )}
                <p className="text-[10px] text-muted-foreground mt-2">JPG/PNG. Auto-resized to max 1000px for fast loading.</p>
              </div>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Stylists offering this service</label>
            <div className="max-h-40 overflow-y-auto border rounded-sm p-3 space-y-1">
              {staffList.length === 0 && <p className="text-xs text-muted-foreground">No staff configured.</p>}
              {staffList.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.staff_ids.includes(s.id)} onChange={() => toggleStaff(s.id)} />
                  <span>{s.name} — {s.role}</span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Tip: if only one stylist is selected, they will be auto-assigned when a client books this service.</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} id="active" />
              <label htmlFor="active" className="text-sm">Active</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} id="featured" />
              <label htmlFor="featured" className="text-sm">⭐ Bestseller</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox checked={form.is_new} onCheckedChange={(v) => setForm({ ...form, is_new: !!v })} id="is_new" />
              <label htmlFor="is_new" className="text-sm">🆕 New</label>
            </div>
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e]">
            {saving ? 'Saving...' : (editing ? 'Update' : 'Create')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============ STAFF TAB ============
const StaffTab = ({ data, refresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await api.delete(`/admin/staff/${id}`);
      toast.success('Staff deleted');
      refresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-serif font-bold text-[#0d3b25]">Staff</h2>
        <Button
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e] border border-[#c5a059] rounded-full"
        >
          <Plus className="w-4 h-4 mr-2" /> New Staff
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {data.staff.map((s) => (
          <div key={s.id} className="bg-white border border-[#c5a059]/20 rounded-sm p-6">
            {s.photo_url ? (
              <img
                src={s.photo_url}
                alt={s.name}
                className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-2 border-[#c5a059]"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#0d3b25] text-[#c5a059] flex items-center justify-center mx-auto mb-4 font-serif font-bold text-2xl">
                {s.name.charAt(0)}
              </div>
            )}
            <h3 className="font-serif font-semibold text-center text-[#0d3b25]">{s.name}</h3>
            <p className="text-xs text-[#c5a059] text-center mb-3">{s.role}</p>
            <p className="text-xs text-muted-foreground text-center mb-4 line-clamp-2">{s.bio}</p>
            <div className="flex space-x-2">
              <Button
                size="sm"
                onClick={() => { setEditing(s); setDialogOpen(true); }}
                className="flex-1 bg-yellow-400 text-[#0d3b25] hover:bg-yellow-500"
              >
                <Edit2 className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button size="sm" onClick={() => handleDelete(s.id)} className="flex-1 bg-red-500 hover:bg-red-600">
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
      <StaffDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} refresh={refresh} />
    </div>
  );
};

const StaffDialog = ({ open, onClose, editing, refresh }) => {
  const [form, setForm] = useState({ name: '', role: '', bio: '', photo_url: '', service_ids: [], active: true });
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (open) {
      api.get('/admin/services').then((r) => setServices(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || '',
        role: editing.role || '',
        bio: editing.bio || '',
        photo_url: editing.photo_url || '',
        service_ids: editing.service_ids || [],
        active: editing.active !== false,
      });
    } else {
      setForm({ name: '', role: '', bio: '', photo_url: '', service_ids: [], active: true });
    }
  }, [editing, open]);

  const toggleService = (sid) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(sid) ? f.service_ids.filter((x) => x !== sid) : [...f.service_ids, sid]
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/staff/${editing.id}`, form);
        toast.success('Staff updated');
      } else {
        await api.post('/admin/staff', form);
        toast.success('Staff created');
      }
      onClose();
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? 'Edit Staff' : 'New Staff'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-4">
            {form.photo_url ? (
              <img src={form.photo_url} alt="preview" className="w-16 h-16 rounded-full object-cover border-2 border-[#c5a059]" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#0d3b25] text-[#c5a059] flex items-center justify-center font-serif font-bold text-xl">
                {(form.name || '?').charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <label className="text-sm font-medium block mb-2">Photo URL</label>
              <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." data-testid="staff-photo-url" />
            </div>
          </div>
          <div><label className="text-sm font-medium block mb-2">Name</label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><label className="text-sm font-medium block mb-2">Role</label><Input required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></div>
          <div><label className="text-sm font-medium block mb-2">Bio</label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
          <div>
            <label className="text-sm font-medium block mb-2">Services this stylist offers</label>
            <div className="max-h-40 overflow-y-auto border rounded-sm p-3 space-y-1">
              {services.length === 0 && <p className="text-xs text-muted-foreground">No services yet.</p>}
              {services.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleService(s.id)} />
                  <span>{s.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} id="staff-active" />
            <label htmlFor="staff-active" className="text-sm">Active</label>
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e]">
            {saving ? 'Saving...' : (editing ? 'Update' : 'Create')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============ CALENDAR TAB ============
const CalendarTab = ({ data, refresh }) => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('upcoming'); // 'upcoming' | 'history'
  const [dateFilter, setDateFilter] = useState('today'); // 'today' | 'tomorrow' | 'week' | 'month' | 'all'
  const [cancelling, setCancelling] = useState(null);
  const [cancelForm, setCancelForm] = useState({ reason: '', custom_message: '' });
  const [cancelBusy, setCancelBusy] = useState(false);
  const [noShow, setNoShow] = useState(null);
  const [noShowMessage, setNoShowMessage] = useState('');
  const [noShowBusy, setNoShowBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };
  const todayStr = new Date().toISOString().split('T')[0];
  const tomorrowStr = addDays(1);
  const weekAheadStr = addDays(7);
  const monthAheadStr = addDays(30);

  const DATE_FILTERS = [
    { key: 'today', label: 'Today' },
    { key: 'tomorrow', label: 'Tomorrow' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'all', label: 'All' },
  ];

  const bookings = data.bookings
    .filter((b) => {
      if (view === 'upcoming') return b.date >= todayStr;
      return b.date < todayStr;
    })
    .filter((b) => {
      if (dateFilter === 'today') return b.date === todayStr;
      if (dateFilter === 'tomorrow') return b.date === tomorrowStr;
      if (dateFilter === 'week') return b.date >= todayStr && b.date <= weekAheadStr;
      if (dateFilter === 'month') return b.date >= todayStr && b.date <= monthAheadStr;
      return true;
    })
    .filter((b) => filter === 'all' || b.status === filter)
    .filter((b) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (b.client_name || '').toLowerCase().includes(q)
        || (b.client_email || '').toLowerCase().includes(q)
        || (b.date || '').includes(q)
        || (b.time || '').includes(q);
    })
    .sort((a, b) => {
      const aKey = `${a.date} ${a.time || ''}`;
      const bKey = `${b.date} ${b.time || ''}`;
      return view === 'upcoming' ? aKey.localeCompare(bKey) : bKey.localeCompare(aKey);
    });

  const completeBooking = async (b) => {
    setCompletingId(b.id);
    try {
      await api.post(`/admin/bookings/${b.id}/complete`);
      toast.success('Appointment marked as completed');
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to complete');
    } finally { setCompletingId(null); }
  };

  const openCancel = (b) => {
    setCancelling(b);
    setCancelForm({
      reason: '',
      custom_message: `Your appointment on ${b.date} at ${b.time} has been cancelled by the salon. We'll contact you as soon as possible to reschedule.`,
    });
  };

  const openNoShow = (b) => {
    setNoShow(b);
    setNoShowMessage(`We missed you on ${b.date} at ${b.time}. Please contact us to reschedule your appointment.`);
  };

  const submitNoShow = async () => {
    setNoShowBusy(true);
    try {
      await api.post(`/admin/bookings/${noShow.id}/no-show`, { custom_message: noShowMessage });
      toast.success('Marked as no-show — customer notified by email');
      setNoShow(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setNoShowBusy(false); }
  };

  const submitCancel = async () => {
    if (!cancelForm.reason.trim()) { toast.error('Please provide a reason'); return; }
    setCancelBusy(true);
    try {
      await api.delete(`/admin/bookings/${cancelling.id}`, { data: cancelForm });
      toast.success('Booking cancelled and client notified by email');
      setCancelling(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="text-3xl font-serif font-bold text-[#0d3b25]">Calendar / Appointments</h2>
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client, service, date..."
            className="w-64"
            data-testid="admin-calendar-search"
          />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="p-2 border rounded-sm text-sm bg-white text-[#0d3b25]" data-testid="calendar-status-filter">
            <option value="all">All Statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
            <option value="no-show">No-Show</option>
          </select>
        </div>
      </div>

      {/* Date range filter pills */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="calendar-date-filters">
        {DATE_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setDateFilter(f.key)}
            data-testid={`calendar-datefilter-${f.key}`}
            className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-full border transition-colors ${
              dateFilter === f.key ? 'bg-[#c5a059] text-[#04140e] border-[#c5a059] font-semibold' : 'bg-white text-[#0d3b25] border-[#c5a059]/40 hover:bg-[#c5a059]/10'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mb-4 inline-flex rounded-full border border-[#c5a059]/40 overflow-hidden bg-white" data-testid="calendar-view-tabs">
        <button
          onClick={() => setView('upcoming')}
          data-testid="calendar-tab-upcoming"
          className={`px-5 py-1.5 text-xs uppercase tracking-widest transition-colors ${
            view === 'upcoming' ? 'bg-[#0d3b25] text-[#c5a059]' : 'text-[#0d3b25] hover:bg-[#c5a059]/10'
          }`}
        >
          Upcoming
        </button>
        <button
          onClick={() => setView('history')}
          data-testid="calendar-tab-history"
          className={`px-5 py-1.5 text-xs uppercase tracking-widest transition-colors ${
            view === 'history' ? 'bg-[#0d3b25] text-[#c5a059]' : 'text-[#0d3b25] hover:bg-[#c5a059]/10'
          }`}
        >
          History
        </button>
      </div>
      <div className="bg-white border border-[#c5a059]/20 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0d3b25] text-[#c5a059]">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="px-6 py-4">Date</th>
              <th className="px-6 py-4">Time</th>
              <th className="px-6 py-4">Client</th>
              <th className="px-6 py-4">Service / Bingo</th>
              <th className="px-6 py-4">Staff</th>
              <th className="px-6 py-4">Total</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#c5a059]/10">
            {bookings.length === 0 ? (
              <tr><td colSpan="8" className="px-6 py-12 text-center text-muted-foreground">No bookings.</td></tr>
            ) : bookings.map((b) => {
              const staff = b.staff || data.staff.find((s) => s.id === b.staff_id);
              const svcNames = (b.services || []).map((s) => s.name).join(', ');
              return (
                <tr key={b.id}>
                  <td className="px-6 py-4 text-sm">{b.date}</td>
                  <td className="px-6 py-4 text-sm">{b.time}</td>
                  <td className="px-6 py-4 text-sm"><div className="font-medium">{b.client_name}</div><div className="text-xs text-muted-foreground">{b.client_email}</div></td>
                  <td className="px-6 py-4 text-xs">
                    {b.promo?.title && <div className="bg-[#c5a059]/20 text-[#0d3b25] text-[10px] px-2 py-0.5 rounded-full inline-block mb-1 font-semibold">BINGO: {b.promo.title}</div>}
                    <div>{svcNames || `${b.service_ids?.length || 0} services`}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">{staff?.name || '—'}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-[#c5a059]">€{b.total_price?.toFixed(2)}</td>
                  <td className="px-6 py-4"><StatusBadge status={b.status} /></td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      {b.status !== 'cancelled' && b.status !== 'no-show' && b.status !== 'completed' && (
                        <>
                          <button
                            onClick={() => completeBooking(b)}
                            disabled={completingId === b.id}
                            data-testid={`complete-booking-${b.id}`}
                            className="bg-green-600 text-white px-3 py-1 text-xs rounded hover:bg-green-700 inline-flex items-center gap-1 justify-center disabled:opacity-50"
                          >
                            <CheckCircle className="w-3 h-3" /> {completingId === b.id ? '...' : 'Complete'}
                          </button>
                          {(() => {
                            const bDate = new Date(`${b.date}T${b.time || '00:00'}`);
                            const now = new Date();
                            const isFuture = bDate > now;
                            return isFuture ? (
                              <button
                                onClick={() => openCancel(b)}
                                data-testid={`cancel-booking-${b.id}`}
                                className="bg-red-500 text-white px-3 py-1 text-xs rounded hover:bg-red-600"
                              >
                                Cancel
                              </button>
                            ) : (
                              <button
                                onClick={() => openNoShow(b)}
                                data-testid={`noshow-booking-${b.id}`}
                                className="bg-orange-500 text-white px-3 py-1 text-xs rounded hover:bg-orange-600 inline-flex items-center gap-1 justify-center"
                              >
                                <AlertTriangle className="w-3 h-3" /> No-Show
                              </button>
                            );
                          })()}
                        </>
                      )}
                      {b.cancellation_reason && (
                        <div className="text-[10px] text-muted-foreground italic">Reason: {b.cancellation_reason}</div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!cancelling} onOpenChange={(v) => !v && setCancelling(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel Booking</DialogTitle></DialogHeader>
          {cancelling && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm">
                <p><strong>{cancelling.client_name}</strong> — {cancelling.date} at {cancelling.time}</p>
                <p className="text-xs text-muted-foreground">{cancelling.client_email}</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Reason for cancellation *</label>
                <Input
                  value={cancelForm.reason}
                  onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
                  placeholder="e.g. Stylist sick, double booking, etc."
                  data-testid="cancel-reason-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">This reason is stored internally and shared with the customer.</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Message to client (you can edit)</label>
                <Textarea
                  rows={5}
                  value={cancelForm.custom_message}
                  onChange={(e) => setCancelForm({ ...cancelForm, custom_message: e.target.value })}
                  data-testid="cancel-custom-message"
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setCancelling(null)} variant="outline" className="flex-1">Back</Button>
                <Button onClick={submitCancel} disabled={cancelBusy} className="flex-1 bg-red-500 text-white hover:bg-red-600" data-testid="cancel-confirm-btn">
                  {cancelBusy ? 'Sending...' : 'Confirm Cancellation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* No-Show Dialog */}
      <Dialog open={!!noShow} onOpenChange={(v) => !v && setNoShow(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark as No-Show</DialogTitle></DialogHeader>
          {noShow && (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 text-sm">
                <p><strong>{noShow.client_name}</strong> — {noShow.date} at {noShow.time}</p>
                <p className="text-xs text-muted-foreground">{noShow.client_email}</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Message to client (sent via email)</label>
                <Textarea rows={5} value={noShowMessage} onChange={(e) => setNoShowMessage(e.target.value)} data-testid="noshow-message" />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setNoShow(null)} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={submitNoShow} disabled={noShowBusy} className="flex-1 bg-orange-500 text-white hover:bg-orange-600" data-testid="noshow-confirm-btn">
                  {noShowBusy ? 'Sending...' : 'Mark No-Show & Notify'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Booking Dialog */}
      <ManualBookingDialog open={manualOpen} onClose={() => setManualOpen(false)} data={data} refresh={refresh} />
    </div>
  );
};

// ============ MANUAL BOOKING DIALOG ============
const ManualBookingDialog = ({ open, onClose, data, refresh }) => {
  const [form, setForm] = useState({
    client_name: '', client_email: '', client_phone: '',
    service_ids: [], staff_id: '',
    date: '', time: '', notes: '',
    payment_method: 'salon', skip_notifications: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({
    client_name: '', client_email: '', client_phone: '',
    service_ids: [], staff_id: '',
    date: '', time: '', notes: '',
    payment_method: 'salon', skip_notifications: false,
  }); }, [open]);

  const toggleService = (id) => setForm((f) => ({
    ...f,
    service_ids: f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id],
  }));

  const submit = async () => {
    if (!form.client_name || !form.client_email || form.service_ids.length === 0 || !form.staff_id || !form.date || !form.time) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/bookings/manual', form);
      toast.success('Booking created' + (form.skip_notifications ? '' : ' — client notified'));
      onClose();
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Manual Booking</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs block mb-1">Client Name *</label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="manual-client-name" /></div>
            <div><label className="text-xs block mb-1">Email *</label>
              <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="manual-client-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs block mb-1">Phone</label>
              <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} data-testid="manual-client-phone" /></div>
            <div><label className="text-xs block mb-1">Stylist *</label>
              <select value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} className="w-full p-2 border rounded-sm text-sm" data-testid="manual-staff">
                <option value="">Select stylist...</option>
                {data.staff.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select></div>
          </div>
          <div>
            <label className="text-xs block mb-1">Services *</label>
            <div className="max-h-40 overflow-y-auto border rounded-sm p-2 space-y-1">
              {data.services.filter((s) => s.active).map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleService(s.id)} data-testid={`manual-svc-${s.id}`} />
                  <span className="flex-1">{s.name}</span>
                  <span className="text-xs text-[#c5a059]">€{s.price} · {s.duration}m</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs block mb-1">Date *</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} min={new Date().toISOString().split('T')[0]} data-testid="manual-date" /></div>
            <div><label className="text-xs block mb-1">Time *</label>
              <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="manual-time" /></div>
          </div>
          <div><label className="text-xs block mb-1">Notes</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.skip_notifications} onCheckedChange={(v) => setForm({ ...form, skip_notifications: !!v })} data-testid="manual-skip-notify" />
            <span>Skip email/SMS notifications to client</span>
          </label>
          <Button onClick={submit} disabled={saving} className="w-full bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e]" data-testid="manual-submit">
            {saving ? 'Creating...' : 'Create Booking'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
const PromotionsTab = ({ data, refresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const filteredPromos = data.promotions.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.title || p.name || '').toLowerCase().includes(q) ||
      (p.title_en || '').toLowerCase().includes(q) ||
      (p.title_fr || '').toLowerCase().includes(q) ||
      (p.type || '').toLowerCase().includes(q)
    );
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await api.delete(`/admin/promotions/${id}`);
      toast.success('Promotion deleted');
      refresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="text-3xl font-serif font-bold text-[#0d3b25]">Promotions / Bingo Combos</h2>
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search promo..."
            className="w-64"
            data-testid="admin-promo-search"
          />
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="admin-create-promo-btn"
            className="bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e] border border-[#c5a059] rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" /> New Combo
          </Button>
        </div>
      </div>
      <div className="bg-white border border-[#c5a059]/20 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0d3b25] text-[#c5a059]">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="px-6 py-4">ID</th>
              <th className="px-6 py-4">Title</th>
              <th className="px-6 py-4">Services</th>
              <th className="px-6 py-4">Staff</th>
              <th className="px-6 py-4">Total</th>
              <th className="px-6 py-4">Offer</th>
              <th className="px-6 py-4">Time</th>
              <th className="px-6 py-4">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#c5a059]/10">
            {filteredPromos.map((p, i) => {
              const serviceNames = (p.service_ids || []).map((sid) => data.services.find((s) => s.id === sid)?.name).filter(Boolean);
              const staff = data.staff.find((s) => s.id === p.staff_id);
              return (
                <tr key={p.id}>
                  <td className="px-6 py-4 text-sm">{p.sort_order || i + 1}</td>
                  <td className="px-6 py-4 text-sm font-medium max-w-xs">{p.title || p.name}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {serviceNames.map((n, k) => (
                        <span key={k} className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">{n}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {staff ? <span className="bg-blue-500 text-white text-xs px-3 py-1 rounded-full">{staff.name}</span> : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm">€{p.total_price?.toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-green-600">€{p.offer_price?.toFixed(2)}</td>
                  <td className="px-6 py-4"><span className="bg-cyan-100 text-cyan-800 text-xs px-2 py-1 rounded">{p.duration}</span></td>
                  <td className="px-6 py-4 space-x-2">
                    <button onClick={() => { setEditing(p); setDialogOpen(true); }} className="bg-yellow-400 text-[#0d3b25] px-3 py-1 text-xs rounded hover:bg-yellow-500">
                      <Edit2 className="w-3 h-3 inline mr-1" />Edit
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="bg-red-500 text-white px-3 py-1 text-xs rounded hover:bg-red-600">
                      <Trash2 className="w-3 h-3 inline mr-1" />Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <PromotionDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} data={data} refresh={refresh} />
    </div>
  );
};

const PromotionDialog = ({ open, onClose, editing, data, refresh }) => {
  const [form, setForm] = useState({
    title: '', title_en: '', title_fr: '',
    description: '', description_en: '', description_fr: '',
    type: 'combo', service_ids: [], staff_id: '',
    offer_price: '', valid_from: '', valid_to: '', active: true, sort_order: 100,
    show_on_landing: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title || editing.name || '',
        title_en: editing.title_en || '',
        title_fr: editing.title_fr || '',
        description: editing.description || '',
        description_en: editing.description_en || '',
        description_fr: editing.description_fr || '',
        type: editing.type || 'combo',
        service_ids: editing.service_ids || [],
        staff_id: editing.staff_id || '',
        offer_price: editing.offer_price || '',
        valid_from: editing.valid_from?.split('T')[0] || '',
        valid_to: editing.valid_to?.split('T')[0] || '',
        active: editing.active !== false,
        sort_order: editing.sort_order || 100,
        show_on_landing: !!editing.show_on_landing,
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      const nextYear = new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().split('T')[0];
      setForm({
        title: '', title_en: '', title_fr: '',
        description: '', description_en: '', description_fr: '',
        type: 'combo', service_ids: [], staff_id: '',
        offer_price: '', valid_from: today, valid_to: nextYear,
        active: true, sort_order: 100, show_on_landing: false
      });
    }
  }, [editing, open]);

  // Calculated fields
  const selectedServices = data.services.filter((s) => form.service_ids.includes(s.id));
  const totalPrice = selectedServices.reduce((sum, s) => sum + s.price, 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration, 0);
  const offerPrice = parseFloat(form.offer_price) || 0;
  const savings = totalPrice - offerPrice;
  const savingsPct = totalPrice > 0 ? Math.round((savings / totalPrice) * 100) : 0;

  const toggleService = (sid) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(sid) ? f.service_ids.filter((x) => x !== sid) : [...f.service_ids, sid]
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.service_ids.length === 0) { toast.error('Select at least one service'); return; }
    setSaving(true);
    const payload = {
      ...form,
      offer_price: offerPrice,
      total_price: totalPrice,
      duration: totalDuration,
      discount_value: savings,
      valid_from: new Date(form.valid_from).toISOString(),
      valid_to: new Date(form.valid_to).toISOString(),
    };
    try {
      if (editing) {
        await api.put(`/admin/promotions/${editing.id}`, payload);
        toast.success('Promotion updated');
      } else {
        await api.post('/admin/promotions', payload);
        toast.success('Promotion created');
      }
      onClose(); refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? 'Edit Combo/Bingo' : 'New Combo/Bingo Offer'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><label className="text-sm font-medium block mb-2">Title (Dutch)</label>
            <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="bijv. Bingo op Bingo — Dameskapsel" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium block mb-2">Title (English)</label>
              <Input value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
            </div>
            <div><label className="text-sm font-medium block mb-2">Title (French)</label>
              <Input value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium block mb-2">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full p-2 border rounded-sm">
                <option value="combo">Combo</option><option value="bingo">Bingo</option>
              </select>
            </div>
            <div><label className="text-sm font-medium block mb-2">Assigned Staff</label>
              <select value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} className="w-full p-2 border rounded-sm">
                <option value="">On-demand</option>
                {data.staff.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Services (select multiple)</label>
            <div className="max-h-48 overflow-y-auto border rounded-sm p-3 space-y-2">
              {data.services.map((s) => (
                <label key={s.id} className="flex items-center space-x-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.service_ids.includes(s.id)} onChange={() => toggleService(s.id)} />
                  <span>{s.name} — €{s.price} ({s.duration}min)</span>
                </label>
              ))}
            </div>
          </div>
          {/* Auto-calc summary */}
          <div className="bg-[#faf6ec] border border-[#c5a059]/30 rounded-sm p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Total Price (P<sub>Original</sub>):</span><span className="font-semibold">€{totalPrice.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Total Duration (T<sub>Total</sub>):</span><span className="font-semibold">{totalDuration} min</span></div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Offer Price (€) *</label>
            <Input type="number" step="0.01" required value={form.offer_price} onChange={(e) => setForm({ ...form, offer_price: e.target.value })} />
          </div>
          {offerPrice > 0 && totalPrice > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-sm">
              <div className="flex justify-between"><span>Savings:</span><span className="font-bold text-green-700">€{savings.toFixed(2)} ({savingsPct}%)</span></div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-4">
            <div><label className="text-sm font-medium block mb-2">Valid From</label><Input type="date" required value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></div>
            <div><label className="text-sm font-medium block mb-2">Valid To</label><Input type="date" required value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} /></div>
            <div><label className="text-sm font-medium block mb-2">Sort Order</label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 100 })} /></div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Description (Dutch)</label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Korte omschrijving voor de klant" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium block mb-2">Description (English)</label>
              <Textarea value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
            </div>
            <div><label className="text-sm font-medium block mb-2">Description (French)</label>
              <Textarea value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} id="promo-active" />
            <label htmlFor="promo-active" className="text-sm">Active</label>
          </div>
          <div className="flex items-center space-x-2 border-t pt-3">
            <Checkbox
              checked={form.show_on_landing}
              onCheckedChange={(v) => setForm({ ...form, show_on_landing: !!v })}
              id="promo-landing"
              data-testid="promo-show-on-landing"
            />
            <label htmlFor="promo-landing" className="text-sm">
              <span className="font-semibold text-[#0d3b25]">Show on landing page popup</span>
              <span className="block text-[10px] text-muted-foreground">Up to 5 deals with this flag will appear in the homepage popup (sorted by Sort Order).</span>
            </label>
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e]">
            {saving ? 'Saving...' : (editing ? 'Update Combo' : 'Create Combo')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============ CUSTOMERS TAB ============
const CustomersTab = ({ data, refresh }) => {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [newForm, setNewForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [profileFor, setProfileFor] = useState(null);

  const filteredCustomers = data.customers.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q)
    );
  });

  const startEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name || '', email: c.email || '', phone: c.phone || '' });
  };

  const viewHistory = (customer) => setProfileFor(customer);

  const exportAllExcel = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/export.csv`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = 'customers.csv';
      a.click();
      URL.revokeObjectURL(href);
      toast.success('Excel/CSV export gedownload');
    } catch {
      toast.error('Export mislukt');
    }
  };

  const createCustomer = async () => {
    if (!newForm.name || !newForm.email) {
      toast.error('Name and email are required');
      return;
    }
    setSaving(true);
    try {
      const { data: res } = await api.post('/admin/customers', newForm);
      toast.success('Customer profile created');
      setNewForm({ name: '', email: '', phone: '', notes: '' });
      setCreating(false);
      setProfileFor(res);
      refresh && refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/customers/${encodeURIComponent(editing.email)}`, form);
      toast.success('Customer updated');
      setEditing(null);
      refresh && refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const deleteCustomer = async (email) => {
    if (!window.confirm(`GDPR erasure: delete all data for ${email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/customers/${encodeURIComponent(email)}`);
      toast.success('Customer erased');
      refresh && refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCustomer = async (email) => {
    try {
      const { data } = await api.get(`/admin/customers/${encodeURIComponent(email)}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${email}-gdpr-export.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="text-3xl font-serif font-bold text-[#0d3b25]">Customers</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">GDPR compliant</span>
          <Button
            onClick={exportAllExcel}
            data-testid="admin-export-excel-btn"
            className="bg-green-600 text-white hover:bg-green-700 rounded-full"
          >
            <Download className="w-4 h-4 mr-2" /> Excel Export
          </Button>
          <Button
            onClick={() => setCreating(true)}
            data-testid="admin-new-customer-btn"
            className="bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e] border border-[#c5a059] rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" /> New Customer
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email or phone..."
          data-testid="admin-customer-search"
          className="max-w-md"
        />
      </div>

      <div className="bg-white border border-[#c5a059]/20 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0d3b25] text-[#c5a059]">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Phone</th>
              <th className="px-6 py-4">Joined</th>
              <th className="px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#c5a059]/10">
            {filteredCustomers.length === 0 ? (
              <tr><td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">Geen klanten gevonden.</td></tr>
            ) : filteredCustomers.map((c) => (
              <tr key={c._id || c.email}>
                <td className="px-6 py-4 text-sm font-medium"><button type="button" onClick={() => viewHistory(c)} className="text-left hover:text-[#c5a059]" data-testid={`open-customer-profile-${c.email}`}>{c.name}</button></td>
                <td className="px-6 py-4 text-sm">{c.email}</td>
                <td className="px-6 py-4 text-sm">{c.phone || '—'}</td>
                <td className="px-6 py-4 text-sm">{c.created_at?.split('T')[0]}</td>
                <td className="px-6 py-4 space-x-1 whitespace-nowrap">
                  <button onClick={() => startEdit(c)} className="bg-yellow-400 text-[#0d3b25] px-3 py-1 text-xs rounded hover:bg-yellow-500" data-testid={`edit-customer-${c.email}`}>Edit</button>
                  <button onClick={() => viewHistory(c)} className="bg-blue-500 text-white px-3 py-1 text-xs rounded hover:bg-blue-600" data-testid={`history-customer-${c.email}`}>History</button>
                  <button onClick={() => deleteCustomer(c.email)} className="bg-red-500 text-white px-3 py-1 text-xs rounded hover:bg-red-600" data-testid={`erase-customer-${c.email}`}>Erase</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium block mb-2">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Email</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <Button onClick={saveEdit} disabled={saving} className="w-full bg-[#0d3b25] text-[#c5a059]">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={(v) => !v && setCreating(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nieuwe klant aanmaken</DialogTitle></DialogHeader>
          <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-2">Naam *</label>
                <Input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} data-testid="new-customer-name" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Email *</label>
                <Input type="email" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} data-testid="new-customer-email" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Telefoon</label>
                <Input value={newForm.phone} onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })} data-testid="new-customer-phone" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Profile notes</label>
                <Textarea rows={3} value={newForm.notes} onChange={(e) => setNewForm({ ...newForm, notes: e.target.value })} placeholder="Important preferences or background…" data-testid="new-customer-profile-notes" />
              </div>
              <Button onClick={createCustomer} disabled={saving} className="w-full bg-[#0d3b25] text-[#c5a059]" data-testid="new-customer-submit">
                {saving ? 'Creating...' : 'Create Customer'}
              </Button>
          </div>
        </DialogContent>
      </Dialog>
      <CustomerProfileDialog customer={profileFor} onClose={() => setProfileFor(null)} onChanged={refresh} />
    </div>
  );
};

export default AdminDashboard;

// ============ AVAILABILITY / TIME SLOTS ============
const WEEKDAYS = [
  { key: 1, label: 'Mon', full: 'Monday' },
  { key: 2, label: 'Tue', full: 'Tuesday' },
  { key: 3, label: 'Wed', full: 'Wednesday' },
  { key: 4, label: 'Thu', full: 'Thursday' },
  { key: 5, label: 'Fri', full: 'Friday' },
  { key: 6, label: 'Sat', full: 'Saturday' },
  { key: 0, label: 'Sun', full: 'Sunday' },
];

const AvailabilityTab = ({ data }) => {
  const [staffId, setStaffId] = useState(data.staff[0]?.id || '');
  const [rangeStart, setRangeStart] = useState(new Date().toISOString().split('T')[0]);
  const [rangeEnd, setRangeEnd] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  const [selectedWeekdays, setSelectedWeekdays] = useState([1, 2, 3, 4, 5, 6]);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [action, setAction] = useState('open');
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState([]);
  const [selectedSlotIds, setSelectedSlotIds] = useState(new Set());
  const [slotSearch, setSlotSearch] = useState('');

  const filteredSlots = slots.filter((s) => {
    if (!slotSearch) return true;
    const q = slotSearch.toLowerCase();
    return s.date.includes(q) || s.start_time.includes(q) || (s.action || '').toLowerCase().includes(q);
  });

  const toggleSlotSelection = (id) => {
    setSelectedSlotIds((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };
  const selectAllVisible = (checked) => {
    if (checked) setSelectedSlotIds(new Set(filteredSlots.map((s) => s.id)));
    else setSelectedSlotIds(new Set());
  };

  const bulkDeleteSelected = async () => {
    if (selectedSlotIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedSlotIds.size} selected slots?`)) return;
    try {
      const { data: res } = await api.post('/admin/slots/bulk-delete', { ids: Array.from(selectedSlotIds) });
      toast.success(`${res.deleted} slots deleted`);
      setSelectedSlotIds(new Set());
      loadSlots();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const bulkDeleteRange = async () => {
    if (!window.confirm(`Delete ALL slots for this stylist between ${rangeStart} and ${rangeEnd}?`)) return;
    try {
      const { data: res } = await api.post('/admin/slots/bulk-delete', {
        staff_id: staffId, start_date: rangeStart, end_date: rangeEnd
      });
      toast.success(`${res.deleted} slots deleted`);
      loadSlots();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const loadSlots = async () => {
    try {
      const { data } = await api.get('/admin/slots', { params: { staff_id: staffId } });
      setSlots(data);
    } catch (e) {}
  };
  useEffect(() => { if (staffId) loadSlots(); }, [staffId]);

  const generatedDates = () => {
    const start = new Date(rangeStart);
    const end = new Date(rangeEnd);
    const list = [];
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return list;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (selectedWeekdays.includes(d.getDay())) {
        list.push(d.toISOString().split('T')[0]);
      }
    }
    return list;
  };

  useEffect(() => {
    setPreview(generatedDates());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd, selectedWeekdays]);

  const toggleWeekday = (k) => {
    setSelectedWeekdays((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const submit = async (e) => {
    e.preventDefault();
    const dateList = generatedDates();
    if (!staffId) { toast.error('Please select a stylist'); return; }
    if (dateList.length === 0) { toast.error('No dates in selected period'); return; }
    if (startTime >= endTime) { toast.error('End time must be after start time'); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post('/admin/slots/bulk', {
        staff_id: staffId, dates: dateList, start_time: startTime, end_time: endTime, action
      });
      toast.success(`${res.inserted} slots saved`);
      loadSlots();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const deleteSlot = async (id) => {
    if (!window.confirm('Delete this slot?')) return;
    await api.delete(`/admin/slots/${id}`);
    loadSlots();
  };

  const applyPreset = (preset) => {
    if (preset === 'weekdays') setSelectedWeekdays([1, 2, 3, 4, 5]);
    if (preset === 'weekend') setSelectedWeekdays([6, 0]);
    if (preset === 'all') setSelectedWeekdays([0, 1, 2, 3, 4, 5, 6]);
    if (preset === 'none') setSelectedWeekdays([]);
  };

  return (
    <div>
      <h2 className="text-3xl font-serif font-bold text-[#0d3b25] mb-2">Availability / Time Slots</h2>
      <p className="text-sm text-muted-foreground mb-8">Bulk manage stylist working hours by date range and weekdays.</p>

      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6 mb-6">
        <h3 className="text-lg font-serif font-semibold text-[#0d3b25] mb-6">Bulk Create / Block Slots</h3>
        <form onSubmit={submit} className="space-y-6">
          {/* Row 1: Staff + Action */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">Stylist</label>
              <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="w-full p-2 border rounded-sm" data-testid="avail-staff-select">
                {data.staff.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">Action</label>
              <select value={action} onChange={(e) => setAction(e.target.value)} className="w-full p-2 border rounded-sm" data-testid="avail-action-select">
                <option value="open">Open (available)</option>
                <option value="block">Block (unavailable)</option>
              </select>
            </div>
          </div>

          {/* Row 2: Date range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">Start Date</label>
              <Input type="date" required value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} data-testid="avail-range-start" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">End Date</label>
              <Input type="date" required value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} data-testid="avail-range-end" />
            </div>
          </div>

          {/* Row 3: Weekday toggles */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Repeat on</label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={() => applyPreset('weekdays')} className="text-[#c5a059] hover:underline">Weekdays</button>
                <button type="button" onClick={() => applyPreset('weekend')} className="text-[#c5a059] hover:underline">Weekend</button>
                <button type="button" onClick={() => applyPreset('all')} className="text-[#c5a059] hover:underline">All</button>
                <button type="button" onClick={() => applyPreset('none')} className="text-[#c5a059] hover:underline">None</button>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {WEEKDAYS.map((wd) => {
                const isActive = selectedWeekdays.includes(wd.key);
                return (
                  <button
                    key={wd.key}
                    type="button"
                    onClick={() => toggleWeekday(wd.key)}
                    data-testid={`avail-weekday-${wd.label.toLowerCase()}`}
                    className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${
                      isActive
                        ? 'bg-[#0d3b25] text-[#c5a059] border-[#c5a059]'
                        : 'bg-white text-[#0d3b25] border-[#0d3b25]/20 hover:border-[#c5a059]'
                    }`}
                  >
                    {wd.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Row 4: Time range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">Start Time</label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="avail-start-time" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">End Time</label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="avail-end-time" />
            </div>
          </div>

          {/* Preview */}
          <div className="bg-[#faf6ec] border border-[#c5a059]/30 rounded-sm p-4">
            <p className="text-sm font-medium text-[#0d3b25] mb-1">
              Preview: <span className="text-[#c5a059] font-bold">{preview.length}</span> day(s) will be affected
            </p>
            {preview.length > 0 && preview.length <= 15 && (
              <p className="text-xs text-muted-foreground">{preview.join(', ')}</p>
            )}
            {preview.length > 15 && (
              <p className="text-xs text-muted-foreground">{preview.slice(0, 6).join(', ')} … {preview.slice(-3).join(', ')}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={saving || preview.length === 0}
            className="bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e] border border-[#c5a059] rounded-full px-8"
            data-testid="avail-submit-btn"
          >
            {saving ? 'Saving...' : `Apply to ${preview.length} day(s)`}
          </Button>
        </form>
      </div>

      {/* Existing slots table grouped by date */}
      <div className="bg-white border border-[#c5a059]/20 rounded-sm overflow-hidden">
        <div className="bg-[#0d3b25] text-[#c5a059] px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-sm uppercase tracking-wider">Existing slots for this stylist</h3>
          <div className="flex items-center gap-2">
            <Input
              value={slotSearch}
              onChange={(e) => setSlotSearch(e.target.value)}
              placeholder="Search date/time..."
              className="h-8 text-xs text-[#0d3b25] bg-white placeholder:text-muted-foreground w-48"
              data-testid="slots-search"
            />
            {selectedSlotIds.size > 0 && (
              <Button onClick={bulkDeleteSelected} className="bg-red-500 text-white hover:bg-red-600 h-8 text-xs" data-testid="slots-bulk-delete-selected">
                <Trash2 className="w-3 h-3 mr-1" /> Verwijder geselecteerde ({selectedSlotIds.size})
              </Button>
            )}
            <Button onClick={bulkDeleteRange} className="bg-red-700 text-white hover:bg-red-800 h-8 text-xs" data-testid="slots-bulk-delete-range">
              <Trash2 className="w-3 h-3 mr-1" /> Verwijder hele periode
            </Button>
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-[#faf6ec]">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-3 w-10">
                <input
                  type="checkbox"
                  checked={filteredSlots.length > 0 && selectedSlotIds.size === filteredSlots.length}
                  onChange={(e) => selectAllVisible(e.target.checked)}
                  data-testid="slots-select-all"
                />
              </th>
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3">Time</th>
              <th className="px-6 py-3">Action</th>
              <th className="px-6 py-3">Delete</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#c5a059]/10">
            {filteredSlots.length === 0 ? (
              <tr><td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">Geen slots gevonden.</td></tr>
            ) : filteredSlots.map((slot) => (
              <tr key={slot.id} className={selectedSlotIds.has(slot.id) ? 'bg-yellow-50' : ''}>
                <td className="px-6 py-3">
                  <input
                    type="checkbox"
                    checked={selectedSlotIds.has(slot.id)}
                    onChange={() => toggleSlotSelection(slot.id)}
                    data-testid={`slot-select-${slot.id}`}
                  />
                </td>
                <td className="px-6 py-3 text-sm">{slot.date}</td>
                <td className="px-6 py-3 text-sm">{slot.start_time} – {slot.end_time}</td>
                <td className="px-6 py-3">
                  <span className={`text-xs px-2 py-1 rounded ${slot.action === 'open' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{slot.action}</span>
                </td>
                <td className="px-6 py-3">
                  <button onClick={() => deleteSlot(slot.id)} className="bg-red-500 text-white px-3 py-1 text-xs rounded hover:bg-red-600">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ SETTINGS ============
const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [holForm, setHolForm] = useState({ date: '', label: '' });

  const load = async () => {
    try {
      const s = await api.get('/admin/settings');
      const h = await api.get('/admin/holidays');
      setSettings(s.data);
      setHolidays(h.data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Instellingen opgeslagen');
    } catch (e) { toast.error('Fout bij opslaan'); }
    finally { setSaving(false); }
  };

  const addHoliday = async (e) => {
    e.preventDefault();
    if (!holForm.date || !holForm.label) return;
    await api.post('/admin/holidays', holForm);
    setHolForm({ date: '', label: '' });
    load();
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    await api.delete(`/admin/holidays/${id}`);
    load();
  };

  if (!settings) return <div className="animate-pulse">Loading...</div>;

  const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-serif font-bold text-[#0d3b25]">Settings</h2>

      {/* Opening Hours */}
      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6">
        <h3 className="text-lg font-serif font-semibold text-[#0d3b25] mb-4">Opening Hours</h3>
        <div className="space-y-3">
          {days.map((d) => {
            const h = settings.opening_hours?.[d] || { open: '09:00', close: '18:00', closed: false };
            return (
              <div key={d} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
                <div className="text-sm font-medium capitalize">{d}</div>
                <Input type="time" value={h.open} onChange={(e) => setSettings({...settings, opening_hours: {...settings.opening_hours, [d]: {...h, open: e.target.value}}})} />
                <Input type="time" value={h.close} onChange={(e) => setSettings({...settings, opening_hours: {...settings.opening_hours, [d]: {...h, close: e.target.value}}})} />
                <div className="flex items-center gap-2">
                  <Checkbox checked={h.closed} onCheckedChange={(v) => setSettings({...settings, opening_hours: {...settings.opening_hours, [d]: {...h, closed: !!v}}})} id={`closed-${d}`} />
                  <label htmlFor={`closed-${d}`} className="text-sm">Closed</label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payments */}
      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6">
        <h3 className="text-lg font-serif font-semibold text-[#0d3b25] mb-4">Payments</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium block mb-2">Default deposit (%)</label>
            <Input
              type="number"
              min="1"
              max="100"
              value={settings.deposit_percent ?? 20}
              onChange={(e) => setSettings({ ...settings, deposit_percent: Math.max(1, Math.min(100, parseInt(e.target.value) || 20)) })}
              data-testid="deposit-percent-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1">1–100. Default 20%. Used for Bancontact/Card payments.</p>
          </div>
          <div>
            <label className="text-sm font-medium block mb-3">Available payment methods</label>
            <div className="space-y-2">
              {[
                { key: 'salon', label: 'Pay in salon (Cash / Bancontact on-site)' },
                { key: 'bancontact', label: 'Bancontact / Payconiq (online deposit)' },
                { key: 'card', label: 'Visa / Mastercard (online deposit)' },
              ].map((m) => {
                const on = (settings.payment_methods_enabled || {})[m.key] !== false;
                return (
                  <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`pm-toggle-${m.key}`}>
                    <Checkbox
                      checked={on}
                      onCheckedChange={(v) => setSettings({
                        ...settings,
                        payment_methods_enabled: {
                          ...(settings.payment_methods_enabled || { salon: true, bancontact: true, card: true }),
                          [m.key]: !!v,
                        },
                      })}
                    />
                    <span>{m.label}</span>
                  </label>
                );
              })}
              <p className="text-[10px] text-muted-foreground mt-2">
                Disabled methods no longer appear on the booking form and are also rejected by the backend.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-[#c5a059]/15">
          <label className="flex items-center gap-3 text-sm cursor-pointer" data-testid="gift-vouchers-toggle">
            <Checkbox
              checked={!!settings.gift_vouchers_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, gift_vouchers_enabled: !!v })}
            />
            <span className="font-medium">Enable online Gift Cards (buy via Stripe)</span>
          </label>
          <p className="text-[10px] text-muted-foreground mt-1 ml-7">When on, a "Gift Cards" page appears in the site menu where customers can purchase gift cards. When off, the page and menu link are hidden.</p>
        </div>
      </div>

      {/* Reminders */}
      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6">
        <h3 className="text-lg font-serif font-semibold text-[#0d3b25] mb-4">Booking Window</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-2">Minimum hours in advance</label>
            <Input
              type="number"
              step="0.25"
              min="0"
              max="72"
              value={settings.min_hours_advance ?? 0.5}
              onChange={(e) => setSettings({ ...settings, min_hours_advance: Math.max(0, parseFloat(e.target.value) || 0.5) })}
              data-testid="min-hours-advance-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1">Bookings must be made at least this many hours before appointment. Default 0.5h (30 min).</p>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Reminders (hours before)</label>
            <Input
              value={(settings.reminder_hours_before || []).join(', ')}
              onChange={(e) => setSettings({...settings, reminder_hours_before: e.target.value.split(',').map(x => parseInt(x.trim())).filter(x => !isNaN(x))})}
              placeholder="24, 4"
            />
            <p className="text-[10px] text-muted-foreground mt-1">Comma-separated list. Default: 24 &amp; 4.</p>
          </div>
        </div>
      </div>

      {/* Email Templates */}
      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6">
        <h3 className="text-lg font-serif font-semibold text-[#0d3b25] mb-4">Email Templates (HTML)</h3>
        <p className="text-sm text-muted-foreground mb-4">Leave blank to use the default system templates. Variables like <code>{`{client_name}`}</code>, <code>{`{date}`}</code>, <code>{`{time}`}</code>, <code>{`{service_names}`}</code> are supported depending on the event.</p>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Confirmation Email Template</label>
            <Textarea rows={6} placeholder="<h2>Booking Confirmed</h2><p>Dear {client_name}, your booking on {date} at {time} for {service_names} is confirmed.</p>" value={settings.email_confirmation || ''} onChange={(e) => setSettings({...settings, email_confirmation: e.target.value})} />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Cancellation Email Template</label>
            <Textarea rows={6} placeholder="<h2>Booking Cancelled</h2><p>Dear {client_name}, your booking on {date} at {time} has been cancelled.</p>" value={settings.email_cancellation || ''} onChange={(e) => setSettings({...settings, email_cancellation: e.target.value})} />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">No-Show Email Template</label>
            <Textarea rows={6} placeholder="<h2>We Missed You</h2><p>Dear {client_name}, we noticed you didn't make it to your appointment on {date} at {time}.</p>" value={settings.email_noshow || ''} onChange={(e) => setSettings({...settings, email_noshow: e.target.value})} />
          </div>
        </div>
      </div>
      <Button onClick={saveSettings} disabled={saving} className="bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e]">
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>

      {/* Holidays */}
      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6">
        <h3 className="text-lg font-serif font-semibold text-[#0d3b25] mb-4">Upcoming Holidays / Closures</h3>
        <form onSubmit={addHoliday} className="flex flex-col md:flex-row gap-3 mb-4">
          <Input type="date" required value={holForm.date} onChange={(e) => setHolForm({...holForm, date: e.target.value})} className="md:w-48" />
          <Input required placeholder="Label (e.g. Christmas)" value={holForm.label} onChange={(e) => setHolForm({...holForm, label: e.target.value})} />
          <Button type="submit" className="bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e]"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </form>
        <div className="space-y-2">
          {holidays.length === 0 ? (
            <p className="text-sm text-muted-foreground">No holidays configured.</p>
          ) : holidays.map((h) => (
            <div key={h.id} className="flex items-center justify-between bg-[#faf6ec] p-3 rounded-sm">
              <div><span className="font-medium">{h.date}</span> — {h.label}</div>
              <button onClick={() => deleteHoliday(h.id)} className="bg-red-500 text-white px-3 py-1 text-xs rounded hover:bg-red-600">Delete</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


// ============ GALLERY TAB ============
const GalleryTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ image_url: '', title: '', caption: '', sort_order: 100, active: true });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/gallery');
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) {
      toast.error('File too large (max 2.5MB). Please optimize the photo first.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.image_url) { toast.error('Kies eerst een afbeelding'); return; }
    setSaving(true);
    try {
      await api.post('/admin/gallery', form);
      toast.success('Foto toegevoegd');
      setForm({ image_url: '', title: '', caption: '', sort_order: 100, active: true });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Foto verwijderen?')) return;
    await api.delete(`/admin/gallery/${id}`);
    load();
  };

  return (
    <div>
      <h2 className="text-3xl font-serif font-bold text-[#0d3b25] mb-6">Gallery</h2>

      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6 mb-6">
        <h3 className="text-lg font-serif font-semibold text-[#0d3b25] mb-4">Upload New Photo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-2">Foto (max 2.5MB) of URL</label>
            <input type="file" accept="image/*" onChange={handleFile} data-testid="gallery-file-input" className="block w-full text-sm mb-2" />
            <Input
              value={form.image_url.startsWith('data:') ? '' : form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              placeholder="…of plak een https:// URL"
              data-testid="gallery-url-input"
            />
            {form.image_url && (
              <img src={form.image_url} alt="preview" className="mt-3 max-h-40 object-cover rounded-sm border" />
            )}
          </div>
          <div className="space-y-3">
            <div><label className="text-sm font-medium block mb-2">Titel</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gallery-title-input" />
            </div>
            <div><label className="text-sm font-medium block mb-2">Bijschrift</label>
              <Textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
            </div>
            <div><label className="text-sm font-medium block mb-2">Sort order</label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 100 })} />
            </div>
            <Button onClick={submit} disabled={saving} className="w-full bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e]" data-testid="gallery-submit-btn">
              {saving ? 'Uploading...' : 'Add Photo'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loading ? <div className="col-span-full text-center py-12">Loading…</div>
          : items.length === 0 ? <div className="col-span-full text-center py-12 text-muted-foreground">No photos yet.</div>
          : items.map((g) => (
            <div key={g.id} className="border border-[#c5a059]/30 rounded-sm overflow-hidden bg-white group relative">
              <img src={g.image_url} alt={g.title} className="w-full h-40 object-cover" />
              <div className="p-2">
                <p className="text-xs font-semibold text-[#0d3b25] truncate">{g.title || '(geen titel)'}</p>
                <p className="text-[10px] text-muted-foreground truncate">{g.caption}</p>
              </div>
              <button
                onClick={() => remove(g.id)}
                data-testid={`gallery-delete-${g.id}`}
                className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Delete
              </button>
            </div>
          ))
        }
      </div>
    </div>
  );
};


// ============ INBOX TAB (Contact form messages) ============
const InboxTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [sortDir, setSortDir] = useState('desc');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/contact');
      const sorted = [...data].sort((a, b) => sortDir === 'desc' ? (b.created_at || '').localeCompare(a.created_at || '') : (a.created_at || '').localeCompare(b.created_at || ''));
      setItems(sorted);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [sortDir]); // eslint-disable-line

  const openMessage = async (m) => {
    setSelected(m);
    setReply('');
    if (m.status === 'new') {
      try { await api.put(`/admin/contact/${m.id}/read`); load(); } catch {}
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) { toast.error('Reply cannot be empty'); return; }
    setSending(true);
    try {
      await api.post(`/admin/contact/${selected.id}/reply`, { reply });
      toast.success('Reply sent by email');
      setSelected(null);
      setReply('');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try { await api.delete(`/admin/contact/${id}`); load(); } catch {}
  };

  const unreadCount = items.filter((m) => m.status === 'new').length;

  return (
    <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-serif font-bold text-[#0d3b25]">
            Inbox {unreadCount > 0 && <span className="ml-2 inline-flex items-center justify-center w-7 h-7 text-xs bg-red-500 text-white rounded-full">{unreadCount}</span>}
          </h2>
          <div className="flex gap-2">
            <select value={sortDir} onChange={(e) => setSortDir(e.target.value)} className="p-2 border rounded-sm text-xs" data-testid="inbox-sort">
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
            <Button onClick={load} variant="outline" className="rounded-full text-xs">Refresh</Button>
          </div>
        </div>
      <div className="grid grid-cols-1 lg:grid-cols-[380px,1fr] gap-6">
        {/* Message list */}
        <div className="bg-white border border-[#c5a059]/20 rounded-sm overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="inbox-list">
          {loading ? <div className="p-6 text-center text-muted-foreground">Loading…</div>
            : items.length === 0 ? <div className="p-8 text-center text-muted-foreground text-sm">No messages yet.</div>
            : items.map((m) => (
            <button
              key={m.id}
              onClick={() => openMessage(m)}
              data-testid={`inbox-item-${m.id}`}
              className={`w-full text-left px-4 py-3 border-b border-[#c5a059]/10 hover:bg-[#faf6ec] transition-colors ${
                selected?.id === m.id ? 'bg-[#faf6ec]' : ''
              } ${m.status === 'new' ? 'font-semibold' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#0d3b25]">{m.name}</span>
                {m.status === 'new' && <span className="w-2 h-2 rounded-full bg-red-500"></span>}
                {m.status === 'replied' && <Reply className="w-3 h-3 text-green-600" />}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-1">{m.email}</p>
              <p className="text-xs text-muted-foreground truncate mt-1">{m.message}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{m.created_at?.split('T')[0]}</p>
            </button>
          ))}
        </div>

        {/* Message detail + reply */}
        <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6" data-testid="inbox-detail">
          {!selected ? (
            <p className="text-center text-muted-foreground py-16">Select a message to view details.</p>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-serif text-xl font-bold text-[#0d3b25]">{selected.name}</h3>
                  <p className="text-xs text-muted-foreground">{selected.email}{selected.phone ? ` · ${selected.phone}` : ''}</p>
                  <p className="text-[10px] text-muted-foreground">{selected.created_at}</p>
                </div>
                <button onClick={() => remove(selected.id)} className="text-xs text-red-600 hover:underline" data-testid="inbox-delete-btn"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="bg-[#faf6ec] rounded-sm p-4 text-sm text-[#0d3b25] whitespace-pre-wrap mb-4" data-testid="inbox-message-body">
                {selected.message}
              </div>
              {selected.reply && (
                <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-sm text-green-900 whitespace-pre-wrap mb-4">
                  <p className="text-[10px] uppercase tracking-widest text-green-700 mb-1">Your reply</p>
                  {selected.reply}
                </div>
              )}
              <label className="text-xs uppercase tracking-widest text-[#c5a059] mb-1 block">Reply via email</label>
              <Textarea
                rows={6}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type your reply here…"
                data-testid="inbox-reply-input"
              />
              <Button onClick={sendReply} disabled={sending || !reply.trim()} className="mt-3 bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e]" data-testid="inbox-reply-btn">
                <Reply className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send Reply'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
// ============ CATEGORIES TAB (Admin CRUD) ============
const CategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ key: '', name: '', name_en: '', name_nl: '', name_fr: '', sort_order: 100, active: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/categories'); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.key || !form.name) { toast.error('Key and Name are required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/categories', form);
      toast.success('Category saved');
      setForm({ key: '', name: '', name_en: '', name_nl: '', name_fr: '', sort_order: 100, active: true });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete category '${key}'?`)) return;
    try { await api.delete(`/admin/categories/${encodeURIComponent(key)}`); load(); } catch {}
  };

  const edit = (c) => setForm({
    key: c.key, name: c.name || '', name_en: c.name_en || '', name_nl: c.name_nl || '', name_fr: c.name_fr || '',
    sort_order: c.sort_order || 100, active: c.active !== false,
  });

  return (
    <div>
      <h2 className="text-3xl font-serif font-bold text-[#0d3b25] mb-6">Categories</h2>
      <p className="text-sm text-muted-foreground mb-4">Categories power the Services page filter sidebar.</p>

      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6 mb-6">
        <h3 className="text-lg font-serif font-semibold text-[#0d3b25] mb-4">Add / Update Category</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="text-xs block mb-1">Key * (unique, lowercase)</label>
            <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="e.g. womens-hair" data-testid="cat-key-input" />
          </div>
          <div>
            <label className="text-xs block mb-1">Name * (default)</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Women's Hair" data-testid="cat-name-input" />
          </div>
          <div>
            <label className="text-xs block mb-1">Sort order</label>
            <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 100 })} data-testid="cat-sort-input" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div><label className="text-xs block mb-1">English</label>
            <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></div>
          <div><label className="text-xs block mb-1">Nederlands</label>
            <Input value={form.name_nl} onChange={(e) => setForm({ ...form, name_nl: e.target.value })} /></div>
          <div><label className="text-xs block mb-1">Français</label>
            <Input value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} /></div>
        </div>
        <label className="flex items-center gap-2 text-sm mb-3">
          <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} />
          <span>Active</span>
        </label>
        <Button onClick={submit} disabled={saving} className="bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2418]" data-testid="cat-save-btn">
          {saving ? 'Saving...' : 'Save Category'}
        </Button>
      </div>

      <div className="bg-white border border-[#c5a059]/20 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0d3b25] text-[#c5a059]">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="px-6 py-4">Key</th>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Sort</th>
              <th className="px-6 py-4">Active</th>
              <th className="px-6 py-4">Actions</th>
            </tr>
          </thead>

            <tr className="bg-yellow-50 hover:bg-yellow-100">
              <td colSpan="6" className="px-6 py-2 text-center text-xs">
                <Button onClick={async () => {
                  try {
                    const defaultCats = [
                      {key: "womens_hair", name: "Women's Hair", name_nl: "Dameskapsels", name_fr: "Coiffure Femme", name_en: "Women's Hair", sort_order: 1},
                      {key: "mens_hair", name: "Barber / Men's Hair", name_nl: "Barbier / Herenkapsel", name_fr: "Coiffure Homme", name_en: "Barber / Men's Hair", sort_order: 2},
                      {key: "hair_coloring", name: "Hair Coloring & Treatments", name_nl: "Haarkleuring & Behandeling", name_fr: "Coloration & Traitements", name_en: "Hair Coloring & Treatments", sort_order: 3},
                      {key: "nail_care", name: "Nail Care", name_nl: "Nagelverzorging", name_fr: "Soins des Ongles", name_en: "Nail Care", sort_order: 4},
                      {key: "waxing", name: "Hair Removal / Waxing", name_nl: "Ontharing / Harsen", name_fr: "Épilation", name_en: "Hair Removal", sort_order: 5},
                      {key: "facial", name: "Facial Services", name_nl: "Gezichtsbehandelingen", name_fr: "Soins du Visage", name_en: "Facial Services", sort_order: 6},
                      {key: "packages", name: "Packages", name_nl: "Pakketten", name_fr: "Forfaits", name_en: "Packages", sort_order: 7}
                    ];
                    for(const c of defaultCats) await api.post('/admin/categories', c);
                    load();
                    toast.success("Default categories seeded!");
                  } catch (e) { toast.error("Error seeding categories"); }
                }} className="h-6 px-3 bg-yellow-200 text-yellow-800 rounded">Load Default Categories</Button>
              </td>
            </tr>
          <tbody className="divide-y divide-[#c5a059]/10">
            {loading ? <tr><td colSpan="5" className="px-6 py-8 text-center">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan="5" className="px-6 py-8 text-center text-muted-foreground">No categories yet.</td></tr>
              : items.map((c) => (
                <tr key={c.key}>
                  <td className="px-6 py-3 text-xs font-mono">{c.key}</td>
                  <td className="px-6 py-3 text-sm">{c.name}</td>
                  <td className="px-6 py-3 text-sm">{c.sort_order ?? '-'}</td>
                  <td className="px-6 py-3 text-sm">{c.active !== false ? '✓' : '—'}</td>
                  <td className="px-6 py-3 space-x-1 whitespace-nowrap">
                    <button onClick={() => edit(c)} className="bg-yellow-400 text-[#0d3b25] px-3 py-1 text-xs rounded" data-testid={`cat-edit-${c.key}`}>Edit</button>
                    <button onClick={() => remove(c.key)} className="bg-red-500 text-white px-3 py-1 text-xs rounded" data-testid={`cat-delete-${c.key}`}>Delete</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ TRANSLATIONS TAB ============
const TranslationsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ key: '', nl: '', en: '', fr: '' });
  const [importBusy, setImportBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Flatten the LanguageContext translations dict into key/en/nl/fr rows
  const flattenAll = () => {
    const rows = [];
    const walk = (oEn, oNl, oFr, prefix) => {
      Object.keys(oEn || {}).forEach((k) => {
        const key = prefix ? `${prefix}.${k}` : k;
        if (oEn[k] && typeof oEn[k] === 'object') {
          walk(oEn[k], (oNl || {})[k] || {}, (oFr || {})[k] || {}, key);
        } else {
          rows.push({ key, en: oEn[k] || '', nl: (oNl || {})[k] || '', fr: (oFr || {})[k] || '' });
        }
      });
    };
    walk(translations.en, translations.nl, translations.fr, '');
    return rows;
  };

  const fillAllSiteText = async (silent = false) => {
    setSeeding(true);
    try {
      const items = flattenAll();
      await api.post('/admin/translations/bulk', { items });
      if (!silent) toast.success(`Loaded ${items.length} translations`);
      await load(true);
    } catch (err) {
      if (!silent) toast.error('Failed to load site text');
    } finally { setSeeding(false); }
  };

  const load = async (skipSeed = false) => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/translations');
      setItems(data);
      if (!skipSeed && data.length === 0) {
        // Auto-populate the table with all site text on first open
        fillAllSiteText(true);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return it.key.toLowerCase().includes(q)
      || (it.nl || '').toLowerCase().includes(q)
      || (it.en || '').toLowerCase().includes(q)
      || (it.fr || '').toLowerCase().includes(q);
  });

  const upsert = async () => {
    if (!form.key.trim()) { toast.error('Key is required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/translations', form);
      toast.success('Translation saved');
      setForm({ key: '', nl: '', en: '', fr: '' });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete translation '${key}'?`)) return;
    await api.delete(`/admin/translations/${encodeURIComponent(key)}`);
    load();
  };

  const editRow = (it) => setForm({ key: it.key, nl: it.nl || '', en: it.en || '', fr: it.fr || '' });

  const downloadExcel = async () => {
    try {
      const res = await api.get('/admin/translations/export.xlsx', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = '4an-translations.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (err) { toast.error('Export failed'); }
  };

  const uploadExcel = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/admin/translations/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Import failed'); }
    finally { setImportBusy(false); e.target.value = ''; }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="text-3xl font-serif font-bold text-[#0d3b25]">Translations</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={downloadExcel} variant="outline" className="rounded-full text-xs" data-testid="translations-export-btn">
            <Download className="w-4 h-4 mr-1" /> Export Excel
          </Button>
          <Button
            onClick={() => fillAllSiteText(false)}
            variant="outline"
            className="rounded-full text-xs bg-yellow-50 text-yellow-800 border-yellow-300"
            disabled={seeding}
            data-testid="translations-load-all-btn"
          >
            {seeding ? 'Loading…' : 'Load All Site Text'}
          </Button>
          <label className="cursor-pointer inline-flex items-center gap-1 bg-[#c5a059] text-[#04140e] text-xs rounded-full px-4 py-2 font-semibold hover:bg-[#d4b56e]" data-testid="translations-import-btn">
            {importBusy ? 'Importing…' : 'Import Excel'}
            <input type="file" accept=".xlsx" onChange={uploadExcel} className="hidden" disabled={importBusy} />
          </label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search key or text..." className="w-64" data-testid="admin-translations-search" />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-4 text-xs text-blue-900">
        <p><strong>Excel format:</strong> Column A = <code>Key (location)</code>, B = English source, C = Nederlands, D = Français. Row 1 is header (skipped on import).</p>
      </div>

      <div className="bg-white border border-[#c5a059]/20 rounded-sm p-6 mb-6">
        <h3 className="text-lg font-serif font-semibold text-[#0d3b25] mb-4">Add / Update</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Translations override the default text in the app. Use dot notation (e.g. <code>booking.submit</code>).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-sm font-medium block mb-2">Key</label>
            <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="booking.submit" data-testid="translation-key-input" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Nederlands</label>
            <Input value={form.nl} onChange={(e) => setForm({ ...form, nl: e.target.value })} data-testid="translation-nl-input" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">English</label>
            <Input value={form.en} onChange={(e) => setForm({ ...form, en: e.target.value })} data-testid="translation-en-input" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Français</label>
            <Input value={form.fr} onChange={(e) => setForm({ ...form, fr: e.target.value })} data-testid="translation-fr-input" />
          </div>
        </div>
        <Button onClick={upsert} disabled={saving} className="mt-4 bg-[#0d3b25] text-[#c5a059] hover:bg-[#0a2e1e]" data-testid="translation-save-btn">
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <div className="bg-white border border-[#c5a059]/20 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0d3b25] text-[#c5a059]">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="px-6 py-4">Key (location)</th>
              <th className="px-6 py-4">EN (source)</th>
              <th className="px-6 py-4">NL</th>
              <th className="px-6 py-4">FR</th>
              <th className="px-6 py-4">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#c5a059]/10">
            {loading ? <tr><td colSpan="5" className="px-6 py-12 text-center">Loading…</td></tr>
              : filtered.length === 0 ? <tr><td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">No translations yet.</td></tr>
              : filtered.map((it) => (
                <tr key={it.key}>
                  <td className="px-6 py-3 text-xs font-mono">{it.key}</td>
                  <td className="px-6 py-3 text-sm">{it.en}</td>
                  <td className="px-6 py-3 text-sm">{it.nl}</td>
                  <td className="px-6 py-3 text-sm">{it.fr}</td>
                  <td className="px-6 py-3 space-x-1 whitespace-nowrap">
                    <button onClick={() => editRow(it)} className="bg-yellow-400 text-[#0d3b25] px-3 py-1 text-xs rounded">Edit</button>
                    <button onClick={() => remove(it.key)} className="bg-red-500 text-white px-3 py-1 text-xs rounded">Delete</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

